import { AllHtmlEntities } from "html-entities";
import {
    unlockScreenOrientation,
    lockScreenOrientationToPortrait,
    enableZoom,
    disableZoom,
} from "./screen";
import logger from "./logger";

export function formatExtension(extension: string): string {
    if (extension.length === 0) {
        return extension;
    }

    if (extension.startsWith(".")) {
        return extension;
    }

    return `.${extension}`;
}

export function formatFileName(name: string, ext: string): string {
    const extension = formatExtension(ext);

    if (extension.length > 0) {
        if (name.endsWith(extension)) {
            return name;
        }
        return `${name}${extension}`;
    }

    return name;
}

/**
 * Number.prototype.format(n, x, s, c)
 *
 * @param integer n: length of decimal
 * @param integer x: length of whole part
 * @param mixed   s: sections delimiter
 * @param mixed   c: decimal delimiter
 */
export const formatNumber = (
    nr: number | undefined,
    decimals: number = 2,
    whole: number = 3,
    sectionDelim: string = " ",
    decimalDelim: string = ","
): string => {
    if (nr === null) {
        return "";
    }
    const re =
        "\\d(?=(\\d{" +
        (whole || 3) +
        "})+" +
        (decimals > 0 ? "\\D" : "$") +
        ")";
    const num = Number(nr).toFixed(Math.max(0, ~~decimals));

    return (decimalDelim ? num.replace(".", decimalDelim) : num).replace(
        new RegExp(re, "g"),
        "$&" + (sectionDelim || ",")
    );
};

const allHtmlEntities = new AllHtmlEntities();
/**
 * Decodes html entities from given string
 * @param text Text to decode. Decodes all html entities in this string.
 * @returns Decoded text
 */
export const decodeHtmlEntities = (text: string | undefined) => {
    return allHtmlEntities.decode(text || "");
};

export const lowToHigh = (a: number, b: number) => {
    if (a > b) {
        return 1;
    }
    if (a < b) {
        return -1;
    }
    return 0;
};

export const highToLow = (a: number, b: number) => {
    if (a > b) {
        return -1;
    }
    if (a < b) {
        return 1;
    }
    return 0;
};

export const autoScroll = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
        const rect = element.getBoundingClientRect();
        const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
        window.scroll({ top: rect.top + scrollTop - 60, behavior: "smooth" });
    }
};

const accessibilityKey = "accessibilitySettingsEnabled";

export const setAccessibilitySetting = (isEnabled: boolean) => {
    const storage = window.localStorage;
    if (isEnabled) {
        logger.log("changing accessibilitysettings to true");
        storage.setItem(accessibilityKey, "true");
        enableZoom();
        unlockScreenOrientation();
    } else {
        logger.log("changing accessibilitysettings to false");
        storage.setItem(accessibilityKey, "false");
        disableZoom();
        lockScreenOrientationToPortrait();
    }
};

export const getAccessibilitySetting = () => {
    return window.localStorage.getItem(accessibilityKey);
};

export const deepEqual = (obj1: Object, obj2: Object): boolean => {
    for (const p of Object.keys(obj1)) {
        if (obj1.hasOwnProperty(p) !== obj2.hasOwnProperty(p)) {
            return false;
        }

        switch (typeof obj1[p]) {
            case "object":
                if (!deepEqual(obj1[p], obj2[p])) {
                    return false;
                }
                break;
            case "function":
                if (
                    typeof obj2[p] === "undefined" ||
                    (p !== "compare" &&
                        obj1[p].toString() !== obj2[p].toString())
                ) {
                    return false;
                }
                break;
            default:
                if (obj1[p] !== obj2[p]) {
                    return false;
                }
        }
    }
    for (const p in obj2) {
        if (typeof obj1[p] === "undefined") {
            return false;
        }
    }
    return true;
};

export const expandReferenceRate = (type: string) => {
    switch (type) {
        case "EUR12": {
            return "Euribor 12 kk";
        }
        case "EUR6": {
            return "Euribor 6 kk";
        }
        case "EUR3": {
            return "Euribor 3 kk";
        }
        case "EUR1": {
            return "Euribor 1 kk";
        }
        case "KIIN": {
            return "Kiinteä";
        }
        default: {
            return type;
        }
    }
};

export function typedKeys<T>(o: T): (keyof T)[] {
    // type cast should be safe because that's what really Object.keys() does
    return Object.keys(o) as (keyof T)[];
}



// WEBPACK FOOTER //
// ./src/util/utils.ts